import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import ProductCard from "../components/ProductCard";
import { CategoryGrid } from "../components/CategoryCard";
import Newsletter from "../components/Newsletter";
import { getProducts } from "../services/woocommerce";
import {
  HERO_IMAGE, PROMO_IMAGE, BENEFITS, BRAND_LOGOS
} from "../data/store";

const SectionHeader = ({ title, to = "/loja" }) => (
  <div className="flex items-end justify-between mb-6">
    <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{title}</h2>
    <Link to={to} className="hidden sm:flex items-center gap-1 text-sm font-semibold text-brand hover:text-brand-hover transition-colors">
      Ver todos <ArrowRight className="h-4 w-4" />
    </Link>
  </div>
);

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);

  useEffect(() => {
    // Busca produtos em destaque
    getProducts({ featured: true, per_page: 8 }).then(setFeaturedProducts).catch(console.error);
    // Para simplificar, buscamos todos e filtramos os mais vendidos depois ou apenas buscamos recentes (o store API permite order by sales)
    getProducts({ orderby: 'popularity', per_page: 8 }).then(setBestSellers).catch(console.error);
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative bg-slate-900" data-testid="hero-banner">
        <img
          src={HERO_IMAGE}
          alt="Materiais de construção"
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl py-16 sm:py-24 lg:py-28">
            <span className="inline-block bg-brand text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded">
              Sulbel Materiais Elétricos
            </span>
            <h1 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Materiais elétricos com qualidade e envio rápido
            </h1>
            <p className="mt-4 text-base sm:text-lg text-slate-200 leading-relaxed max-w-xl">
              Cabos, extensões, terminais e acessórios para instalações elétricas,
              automotivas e energia solar. Enviamos para todo o Brasil.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button asChild className="h-12 px-8 bg-brand hover:bg-brand-hover text-white font-semibold rounded-md active:scale-[0.98] transition-all">
                <Link to="/loja" data-testid="hero-cta-primary">Comprar agora</Link>
              </Button>
              <Button asChild variant="outline" className="h-12 px-8 bg-white/10 text-white border-white/40 hover:bg-white hover:text-slate-900 font-semibold rounded-md transition-all">
                <Link to="/loja?ofertas=1" data-testid="hero-cta-secondary">Ver ofertas</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {BENEFITS.map((b) => {
              const Icon = Icons[b.icon] || Icons.Check;
              return (
                <div key={b.title} className="flex items-start gap-3" data-testid={`benefit-${b.title}`}>
                  <span className="shrink-0 h-11 w-11 rounded-full bg-brand-light flex items-center justify-center">
                    <Icon className="h-5 w-5 text-brand" />
                  </span>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">{b.title}</p>
                    <p className="text-sm text-slate-500 leading-snug">{b.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-6">
          Compre por categoria
        </h2>
        <CategoryGrid />
      </section>

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <SectionHeader title="Produtos em destaque" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {featuredProducts.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>

      {/* Promo banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="relative overflow-hidden rounded-xl bg-slate-900" data-testid="promo-banner">
          <img src={PROMO_IMAGE} alt="Ofertas" className="absolute inset-0 h-full w-full object-cover opacity-30" />
          <div className="relative px-6 sm:px-12 py-12 sm:py-16 max-w-2xl">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
              Economize na sua instalação elétrica
            </h2>
            <p className="mt-3 text-slate-200">
              Confira nossas ofertas em cabos, extensões e acessórios selecionados.
            </p>
            <Button asChild className="mt-6 h-12 px-8 bg-brand hover:bg-brand-hover text-white font-semibold rounded-md">
              <Link to="/loja?ofertas=1" data-testid="promo-cta">Ver ofertas</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Best sellers */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <SectionHeader title="Mais vendidos" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {bestSellers.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>

      {/* Brands */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-6">
          Qualidade e certificações
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
          {BRAND_LOGOS.map((b) => (
            <div
              key={b}
              data-testid={`brand-${b.toLowerCase()}`}
              className="flex items-center justify-center h-20 bg-white border border-slate-200 rounded-lg text-slate-500 font-bold text-lg tracking-tight hover:border-slate-300 hover:text-slate-700 transition-colors"
            >
              {b}
            </div>
          ))}
        </div>
      </section>

      <Newsletter />
    </div>
  );
};

export default Home;
